import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import {
  DataGridPro,
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridRow,
} from '@mui/x-data-grid-pro';
import { useAuth0 } from '@auth0/auth0-react';
import { BookingSection, BookingOption } from '@dreamai/types';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import { ReactComponent as InfoCircleIcon } from '../../../icons/info-circle.svg';
import { ReactComponent as CheckCircleSmIcon } from '../../../icons/check-circle-sm.svg';
import RecommendedBadge from './RecommendedBadge';
import api from '../../../api';

const LONG_TEXT_WORD_LIMIT = 7;

/**
 * Check if a string value exceeds the word limit.
 */
function isLongText(val: any): boolean {
  if (typeof val !== 'string') return false;
  return val.trim().split(/\s+/).length > LONG_TEXT_WORD_LIMIT;
}

/**
 * Cell that shows a "Read" link for long text, opening a modal with the full content.
 */
const ReadMoreCell: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span
        className='booking-read-link'
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Read
      </span>
      {open &&
        createPortal(
          <div
            className='booking-read-modal-overlay'
            onClick={() => setOpen(false)}
          >
            <div
              className='booking-read-modal'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='booking-read-modal-header'>
                <span className='booking-read-modal-title'>{label}</span>
                <button
                  className='booking-read-modal-close'
                  onClick={() => setOpen(false)}
                >
                  &times;
                </button>
              </div>
              <div className='booking-read-modal-body'>{value}</div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

/**
 * Get columns from section parameters, or fall back to deriving from data keys.
 */
export function getColumns(
  section: BookingSection,
  options: BookingOption[],
): { key: string; label: string }[] {
  // Use section parameters if defined
  if (section.parameters && section.parameters.length > 0) {
    return section.parameters.map((p) => ({ key: p.key, label: p.label }));
  }
  // Fallback: derive from option data keys
  const keySet = new Set<string>();
  for (const opt of options) {
    if (opt.data && typeof opt.data === 'object') {
      for (const k of Object.keys(opt.data)) {
        if (k === 'extra_images') continue;
        keySet.add(k);
      }
    }
  }
  const keys = Array.from(keySet);
  keys.sort((a, b) => {
    if (a === 'name') return -1;
    if (b === 'name') return 1;
    return a.localeCompare(b);
  });
  return keys.map((k) => ({
    key: k,
    label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}

/**
 * Check if a value looks like an ISO datetime string.
 */
function isDateTimeValue(val: any): boolean {
  if (typeof val !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val);
}

/**
 * Get the date portion (YYYY-MM-DD) from an ISO datetime string.
 */
function getDatePart(val: string): string {
  return val.slice(0, 10);
}

/**
 * Format ordinal day suffix (1st, 2nd, 3rd, 4th, etc.)
 */
function ordinal(day: number): string {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Format a datetime value as "HH:mm" or "HH:mm - Fri, 20th" if multiple dates.
 */
function formatDateTime(val: string, showDate: boolean): string {
  // Extract time directly from the ISO string to preserve the destination timezone
  const tIdx = val.indexOf('T');
  if (tIdx === -1) return val;
  const time = val.slice(tIdx + 1, tIdx + 6);
  if (!showDate) return time;
  // Parse date parts from the date portion to avoid timezone shifts
  const [yearStr, monthStr, dayStr] = val.slice(0, 10).split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10) - 1;
  const day = parseInt(dayStr, 10);
  // Use Date.UTC to get the day-of-week without local timezone interference
  const dow = new Date(Date.UTC(year, month, day)).getUTCDay();
  return `${time} - ${DAY_NAMES[dow]}, ${ordinal(day)}`;
}

/**
 * For a given column key, check if there are multiple distinct dates across options.
 */
function hasMultipleDates(
  sectionOptions: BookingOption[],
  key: string,
): boolean {
  const dates = new Set<string>();
  for (const opt of sectionOptions) {
    const val = opt.data?.[key];
    if (isDateTimeValue(val)) {
      dates.add(getDatePart(val));
      if (dates.size > 1) return true;
    }
  }
  return false;
}

/**
 * Individual checkbox cell that manages its own re-render independently of the grid.
 */
const SelectCheckbox: React.FC<{
  id: string;
  selectedIdsRef: React.MutableRefObject<Set<string>>;
  toggleSelection: (id: string) => void;
}> = ({ id, selectedIdsRef, toggleSelection }) => {
  const [isSelected, setIsSelected] = useState(selectedIdsRef.current.has(id));

  // Re-sync from ref when the toggle callback changes (e.g. single-select
  // return flights where clicking one must visually un-check the previous)
  useEffect(() => {
    setIsSelected(selectedIdsRef.current.has(id));
  }, [toggleSelection, id, selectedIdsRef]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsSelected((prev) => !prev);
      toggleSelection(id);
    },
    [id, toggleSelection],
  );

  return (
    <div
      className={`booking-select-checkbox${isSelected ? ' selected' : ''}`}
      onClick={handleClick}
      role='button'
      tabIndex={0}
    >
      <TickSelectIcon />
    </div>
  );
};

/**
 * Build a human-readable title for a booking option.
 * Flights get a special format; others use name > title > first column value.
 */
export function getOptionTitle(
  opt: BookingOption,
  section: BookingSection,
  columns: { key: string; label: string }[],
): string {
  const d = opt.data as Record<string, any> | undefined;
  if (!d) return opt.id;

  if (section.view === 'one_way_flights' || section.view === 'return_flights') {
    const dep = d.departure_time;
    const arr = d.arrival_time;
    const from = d.departure_airport || '';
    const to = d.arrival_airport || '';
    const fmtTime = (v: any) => {
      if (!v) return '??:??';
      if (typeof v === 'string' && /T\d{2}:\d{2}/.test(v)) {
        return v.slice(v.indexOf('T') + 1, v.indexOf('T') + 6);
      }
      return String(v);
    };
    return `${fmtTime(dep)} ${from} → ${fmtTime(arr)} ${to}`.trim();
  }

  if (d.name) return String(d.name);
  if (d.title) return String(d.title);

  // First visible column value
  const hiddenFields = new Set(['score', 'highlighted', 'highlighted_reason']);
  for (const col of columns) {
    if (hiddenFields.has(col.key)) continue;
    if (d[col.key] != null && d[col.key] !== '') return String(d[col.key]);
  }
  return opt.id;
}

export interface BookingSectionGridHandle {
  deselectOption: (optionId: string) => void;
  clearReturnSelection: () => void;
}

interface BookingSectionGridProps {
  section: BookingSection;
  sectionOptions: BookingOption[];
  columns: { key: string; label: string }[];
  onSelectionChange?: (slug: string, selectedIds: Set<string>) => void;
  onReturnFlightSelected?: (
    outboundOption: BookingOption | null,
    returnFlight: Record<string, any> | null,
  ) => void;
}

/**
 * Sub-component: renders a single section's options as a MUI DataGrid.
 */
const BookingSectionGrid = forwardRef<
  BookingSectionGridHandle,
  BookingSectionGridProps
>(
  (
    {
      section,
      sectionOptions,
      columns,
      onSelectionChange,
      onReturnFlightSelected,
    },
    ref,
  ) => {
    const { getAccessTokenSilently } = useAuth0();

    // Normal selection state (for non-return_flights sections)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;

    // Return flights state (only used for return_flights sections)
    const isReturnFlights = section.view === 'return_flights';
    const [selectedOutboundId, setSelectedOutboundId] = useState<string | null>(
      null,
    );
    const [returnFlights, setReturnFlights] = useState<Record<string, any>[]>(
      [],
    );
    const [returnFlightsLoading, setReturnFlightsLoading] = useState(false);
    const [returnFlightsError, setReturnFlightsError] = useState<string | null>(
      null,
    );
    const [selectedReturnId, setSelectedReturnId] = useState<string | null>(
      null,
    );
    const [showAllOutbound, setShowAllOutbound] = useState(false);

    // Ref for return flight checkbox visual
    const selectedReturnIdsRef = useRef<Set<string>>(new Set());

    // Expose imperative methods for parent to deselect options.
    // These only update local grid state — they must NOT call
    // onSelectionChange / onReturnFlightSelected to avoid an infinite
    // loop (the replyTo item is already removed by the caller).
    useImperativeHandle(
      ref,
      () => ({
        deselectOption: (optionId: string) => {
          setSelectedIds((prev) => {
            if (!prev.has(optionId)) return prev;
            const next = new Set(prev);
            next.delete(optionId);
            return next;
          });
        },
        clearReturnSelection: () => {
          setSelectedReturnId(null);
          selectedReturnIdsRef.current = new Set();
        },
      }),
      [],
    );

    // Fetch return flights when outbound is selected
    useEffect(() => {
      if (!isReturnFlights || !selectedOutboundId) {
        setReturnFlights([]);
        setReturnFlightsError(null);
        setReturnFlightsLoading(false);
        return;
      }
      const outboundOption = sectionOptions.find(
        (o) => o.id === selectedOutboundId,
      );
      const departureTokenId = outboundOption?.data?.departure_token;
      if (!departureTokenId) {
        setReturnFlightsError('No departure token found for this flight.');
        return;
      }
      let cancelled = false;
      const fetchReturns = async () => {
        setReturnFlightsLoading(true);
        setReturnFlightsError(null);
        setSelectedReturnId(null);
        try {
          const token = await getAccessTokenSilently();
          const res = await api.get(
            `/booking-sections/return-flights/${departureTokenId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (cancelled) return;
          if (res.data?.success) {
            setReturnFlights(res.data.returnFlights || []);
            if ((res.data.returnFlights || []).length === 0) {
              setReturnFlightsError(
                'No return flights found for this departure.',
              );
            }
          } else {
            setReturnFlightsError(
              res.data?.message || 'Failed to load return flights.',
            );
          }
        } catch (err: any) {
          if (cancelled) return;
          setReturnFlightsError(
            err.response?.data?.message || 'Failed to load return flights.',
          );
        } finally {
          if (!cancelled) setReturnFlightsLoading(false);
        }
      };
      fetchReturns();
      return () => {
        cancelled = true;
      };
    }, [
      isReturnFlights,
      selectedOutboundId,
      sectionOptions,
      getAccessTokenSilently,
    ]);

    // Toggle return flight selection (single-select via checkbox)
    const toggleReturnSelection = useCallback(
      (id: string) => {
        setSelectedReturnId((prev) => {
          if (prev === id) {
            selectedReturnIdsRef.current = new Set();
            onReturnFlightSelected?.(null, null);
            return null;
          }
          selectedReturnIdsRef.current = new Set([id]);
          const outbound = sectionOptions.find(
            (o) => o.id === selectedOutboundId,
          );
          const returnData = returnFlights.find((r) => String(r.id) === id);
          if (outbound && returnData) {
            onReturnFlightSelected?.(outbound, returnData);
          }
          return id;
        });
      },
      [
        selectedOutboundId,
        selectedReturnId,
        sectionOptions,
        returnFlights,
        onReturnFlightSelected,
      ],
    );

    const toggleSelection = useCallback(
      (id: string) => {
        if (isReturnFlights) {
          // Single-select for outbound: toggle selectedOutboundId
          setSelectedOutboundId((prev) => {
            const next = prev === id ? null : id;
            if (!next) onReturnFlightSelected?.(null, null);
            setShowAllOutbound(false);
            return next;
          });
          // Keep selectedIds in sync for the checkbox visual
          setSelectedIds((prev) => {
            if (prev.has(id)) return new Set();
            return new Set([id]);
          });
          return;
        }
        setSelectedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) {
            next.delete(id);
          } else {
            next.add(id);
          }
          onSelectionChange?.(section.slug, next);
          return next;
        });
      },
      [
        section.slug,
        onSelectionChange,
        isReturnFlights,
        onReturnFlightSelected,
      ],
    );

    const multiDateCols = useMemo(() => {
      const set = new Set<string>();
      for (const col of columns) {
        if (hasMultipleDates(sectionOptions, col.key)) {
          set.add(col.key);
        }
      }
      return set;
    }, [columns, sectionOptions]);

    const gridColumns: GridColDef[] = useMemo(() => {
      const selectCol: GridColDef = {
        field: '__select__',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params: any) => (
          <SelectCheckbox
            id={params.row.id}
            selectedIdsRef={selectedIdsRef}
            toggleSelection={toggleSelection}
          />
        ),
      };

      const hiddenFields = new Set([
        'score',
        'highlighted',
        'highlighted_reason',
        'departure_token',
        'quick_filter',
        'price_currency',
        // Outbound legs of round trips have no direct booking link
        ...(section.view === 'return_flights' ? ['booking_link'] : []),
      ]);

      const visibleCols = columns.filter((col) => {
        if (hiddenFields.has(col.key)) return false;
        // Hide columns where every row is empty
        return sectionOptions.some(
          (o) => o.data?.[col.key] != null && o.data?.[col.key] !== '',
        );
      });

      const dataCols = visibleCols.map((col, colIndex) => {
        const def: GridColDef = {
          field: col.key,
          headerName: col.label,
          flex: 1,
          minWidth: 120,
        };

        const sampleVal = sectionOptions.find(
          (o) => o.data?.[col.key] !== undefined && o.data?.[col.key] !== null,
        )?.data?.[col.key];

        if (isDateTimeValue(sampleVal)) {
          const showDate = multiDateCols.has(col.key);
          if (colIndex === 0) {
            def.renderCell = (params: any) => {
              const formatted =
                params.value && isDateTimeValue(params.value)
                  ? formatDateTime(params.value, showDate)
                  : (params.value ?? '');
              const isHighlighted = params.row.highlighted;
              const reason = params.row.highlighted_reason;
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    flexWrap: 'wrap',
                    gap: '4px',
                  }}
                >
                  <span>{formatted}</span>
                  {isHighlighted && <RecommendedBadge reason={reason} />}
                </div>
              );
            };
          } else {
            def.valueFormatter = (value: any) => {
              if (!value || !isDateTimeValue(value)) return value ?? '';
              return formatDateTime(value, showDate);
            };
          }
        } else if (col.key === 'price') {
          def.renderCell = (params: any) => {
            const val = params.value;
            if (val == null || val === '') return '';
            const currency = params.row.price_currency;
            if (currency) {
              try {
                return new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(Number(val));
              } catch {
                // fall through to plain display
              }
            }
            return String(val);
          };
        } else if (col.key === 'booking_link') {
          def.renderCell = (params: any) => {
            const url = params.value;
            if (!url) return '';
            return (
              <a href={url} target='_blank' rel='noopener noreferrer'>
                View
              </a>
            );
          };
        } else if (colIndex === 0) {
          def.renderCell = (params: any) => {
            const isHighlighted = params.row.highlighted;
            const reason = params.row.highlighted_reason;
            const val = params.value ?? '';
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '4px',
                }}
              >
                {isLongText(val) ? (
                  <ReadMoreCell value={String(val)} label={col.label} />
                ) : (
                  <span>{val}</span>
                )}
                {isHighlighted && <RecommendedBadge reason={reason} />}
              </div>
            );
          };
        } else {
          // Default: show "Read" link for long text values
          def.renderCell = (params: any) => {
            const val = params.value;
            if (val == null || val === '') return '';
            if (isLongText(val)) {
              return <ReadMoreCell value={String(val)} label={col.label} />;
            }
            return String(val);
          };
        }

        return def;
      });

      return [selectCol, ...dataCols];
    }, [columns, sectionOptions, multiDateCols]);

    const gridRows: GridRowsProp = useMemo(() => {
      const rows: Record<string, any>[] = sectionOptions.map((opt) => ({
        id: opt.id,
        ...opt.data,
      }));
      // Sort highlighted rows to the top
      rows.sort((a, b) => {
        const aH = a.highlighted ? 1 : 0;
        const bH = b.highlighted ? 1 : 0;
        return bH - aH;
      });
      return rows;
    }, [sectionOptions]);

    const initialSortModel: GridSortModel = useMemo(() => {
      if (
        section.view === 'one_way_flights' ||
        section.view === 'return_flights'
      ) {
        return [{ field: 'departure_time', sort: 'asc' as const }];
      }
      return [{ field: 'score', sort: 'desc' as const }];
    }, [section.view]);

    // Return flights grid columns
    const returnGridColumns: GridColDef[] = useMemo(() => {
      if (!isReturnFlights) return [];

      const returnSelectCol: GridColDef = {
        field: '__select__',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        disableColumnMenu: true,
        resizable: false,
        renderCell: (params: any) => (
          <SelectCheckbox
            id={String(params.row.id)}
            selectedIdsRef={selectedReturnIdsRef}
            toggleSelection={toggleReturnSelection}
          />
        ),
      };

      const colDefs: { field: string; headerName: string }[] = [
        { field: 'departure_time', headerName: 'Departure' },
        { field: 'arrival_time', headerName: 'Arrival' },
        { field: 'departure_airport', headerName: 'From' },
        { field: 'arrival_airport', headerName: 'To' },
        { field: 'airlines', headerName: 'Airline' },
        { field: 'duration', headerName: 'Duration' },
        { field: 'stops', headerName: 'Stops' },
        { field: 'price', headerName: 'Total Price' },
      ];
      const cols = colDefs.map((c) => {
        const def: GridColDef = {
          field: c.field,
          headerName: c.headerName,
          flex: 1,
          minWidth: 100,
        };
        if (c.field === 'departure_time' || c.field === 'arrival_time') {
          def.valueFormatter = (value: any) => {
            if (!value || !isDateTimeValue(value)) return value ?? '';
            return formatDateTime(value, false);
          };
        } else if (c.field === 'price') {
          def.renderCell = (params: any) => {
            const val = params.value;
            if (val == null || val === '') return '';
            const currency = params.row.price_currency;
            if (currency) {
              try {
                return new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency,
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(Number(val));
              } catch {
                // fall through
              }
            }
            return String(val);
          };
        }
        return def;
      });
      return [returnSelectCol, ...cols];
    }, [isReturnFlights, toggleReturnSelection]);

    const returnGridRows: GridRowsProp = useMemo(
      () => returnFlights.map((f) => ({ ...f })),
      [returnFlights],
    );

    const getRowClassName = useCallback(
      (params: any) => {
        const classes: string[] = [];
        // Hide non-selected outbound rows when one is picked (unless expanded)
        if (
          isReturnFlights &&
          selectedOutboundId &&
          !showAllOutbound &&
          String(params.id) !== selectedOutboundId
        ) {
          classes.push('booking-row-hidden');
        }
        if (isReturnFlights && selectedOutboundId === String(params.id)) {
          // No highlight for selected outbound
        } else if (selectedIds.has(params.id)) {
          classes.push('booking-row-selected');
        }
        if (params.row.highlighted) classes.push('booking-row-highlighted');
        return classes.join(' ');
      },
      [selectedIds, isReturnFlights, selectedOutboundId, showAllOutbound],
    );

    const dataGridSx = {
      '& .MuiDataGrid-columnHeaderTitle': {
        fontFamily: 'Instrument Sans, sans-serif',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        color: '#0d1f16',
        textTransform: 'none',
        letterSpacing: 'normal',
      },
      '& .MuiDataGrid-cell': {
        fontFamily: 'Instrument Sans, sans-serif',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: '20px',
        color: '#0d1f16',
        padding: '8px 12px',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        display: 'flex',
        alignItems: 'center',
        maxHeight: '56px',
        overflow: 'hidden',
      },
      '& .MuiDataGrid-cell[data-field="__select__"]': {
        padding: '0 8px 0 0',
      },
      '& .MuiDataGrid-columnHeader[data-field="__select__"]': {
        padding: '0 8px 0 0',
      },
      '& .MuiDataGrid-cellEmpty': {
        display: 'none',
      },
    };

    return (
      <>
        {isReturnFlights && !selectedOutboundId && (
          <p className='booking-section-hint'>
            <InfoCircleIcon />
            Select a flight to load return options
          </p>
        )}
        {isReturnFlights &&
          selectedOutboundId &&
          (() => {
            const opt = sectionOptions.find((o) => o.id === selectedOutboundId);
            const d = opt?.data as Record<string, any> | undefined;
            const fmtTime = (v: any) => {
              if (!v) return '';
              if (typeof v === 'string' && /T\d{2}:\d{2}/.test(v))
                return v.slice(v.indexOf('T') + 1, v.indexOf('T') + 6);
              return String(v);
            };
            const label = d
              ? `Selected ${fmtTime(d.departure_time)} ${d.departure_airport || ''}-${d.arrival_airport || ''}`
              : 'Selected';
            return (
              <p className='booking-section-hint booking-section-hint--selected'>
                <CheckCircleSmIcon />
                {label}
              </p>
            );
          })()}
        <DataGridPro
          rows={gridRows}
          columns={gridColumns}
          initialState={{
            sorting: { sortModel: initialSortModel },
          }}
          disableRowSelectionOnClick
          hideFooterPagination
          hideFooter
          autoHeight
          density='compact'
          disableColumnMenu
          getRowClassName={getRowClassName}
          slots={{
            row: GridRow,
          }}
          getRowHeight={() => 'auto'}
          sx={dataGridSx}
        />

        {/* Show/Hide other outbound options link */}
        {isReturnFlights && selectedOutboundId && sectionOptions.length > 1 && (
          <p
            className='booking-section-show-others'
            onClick={() => setShowAllOutbound((prev) => !prev)}
          >
            +{sectionOptions.length - 1} other outbound option
            {sectionOptions.length - 1 !== 1 ? 's' : ''}{' '}
            <span className='booking-section-show-others__link'>
              {showAllOutbound ? 'Hide' : 'Show'}
            </span>
          </p>
        )}

        {/* Return flights section */}
        {isReturnFlights && selectedOutboundId && (
          <div className='return-flights-section'>
            {returnFlightsLoading && (
              <div className='return-flights-loading'>
                <div className='return-flights-spinner' />
                <span>Loading return flights…</span>
              </div>
            )}
            {returnFlightsError && !returnFlightsLoading && (
              <div className='return-flights-error'>{returnFlightsError}</div>
            )}
            {!returnFlightsLoading &&
              !returnFlightsError &&
              returnFlights.length > 0 && (
                <>
                  {selectedReturnId ? (
                    (() => {
                      const rf = returnFlights.find(
                        (r) => String(r.id) === selectedReturnId,
                      );
                      const fmtTime = (v: any) => {
                        if (!v) return '';
                        if (typeof v === 'string' && /T\d{2}:\d{2}/.test(v))
                          return v.slice(
                            v.indexOf('T') + 1,
                            v.indexOf('T') + 6,
                          );
                        return String(v);
                      };
                      const label = rf
                        ? `Selected ${fmtTime(rf.departure_time)} ${rf.departure_airport || ''}-${rf.arrival_airport || ''}`
                        : 'Selected';
                      return (
                        <p className='booking-section-hint booking-section-hint--selected'>
                          <CheckCircleSmIcon />
                          {label}
                        </p>
                      );
                    })()
                  ) : (
                    <p className='booking-section-hint'>
                      <InfoCircleIcon />
                      Select a return flight
                    </p>
                  )}
                  <DataGridPro
                    rows={returnGridRows}
                    columns={returnGridColumns}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: 'departure_time', sort: 'asc' }],
                      },
                    }}
                    disableRowSelectionOnClick
                    hideFooterPagination
                    hideFooter
                    autoHeight
                    density='compact'
                    disableColumnMenu
                    slots={{ row: GridRow }}
                    getRowHeight={() => 'auto'}
                    sx={dataGridSx}
                  />
                </>
              )}
          </div>
        )}
      </>
    );
  },
);

BookingSectionGrid.displayName = 'BookingSectionGrid';

export default BookingSectionGrid;
